import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiCheck, FiClipboard, FiRefreshCw, FiUserCheck, FiX } from 'react-icons/fi';
import { useToast } from '../../components/toast/toastProvider';
import OperationsService, {
  DailySiteDiary,
  LeaveRequest,
  LeaveRequestStatus,
  SnagTask,
  SnagTaskStatus,
} from '../../services/operationsService';
import { SnagTrackerService } from '../../services/snagTrackerService';
import UserGroupsService, { User, UserGroup } from '../../services/userGroupsService';
import { Snag } from '../../shared/types/snag';
import './OperationsPage.css';

type Tab = 'diary' | 'leave' | 'tasks';

const today = () => new Date().toISOString().slice(0, 10);
const monthStart = () => {
  const date = new Date();
  date.setDate(1);
  return date.toISOString().slice(0, 10);
};

const displayUser = (user?: User | { name?: string; username?: string; email?: string }) => {
  const value = user as any;
  return value?.name || value?.username || value?.email || 'Unassigned';
};

const emptyDiaryForm = {
  diaryDate: today(),
  departmentId: '',
  weather: '',
  manpowerSummary: '',
  workCompleted: '',
  delays: '',
  incidents: '',
  deliveries: '',
  notes: '',
  photoUrls: '',
  status: 'submitted' as 'draft' | 'submitted',
};

const emptyLeaveForm = {
  type: 'leave' as 'leave' | 'availability',
  departmentId: '',
  startDate: today(),
  endDate: today(),
  reason: '',
};

const emptyTaskForm = {
  snagId: '',
  title: '',
  description: '',
  assignedToId: '',
  dueDate: '',
};

const OperationsPage: React.FC = () => {
  const { addToast } = useToast();
  const [activeTab, setActiveTab] = useState<Tab>('diary');
  const [loading, setLoading] = useState(false);
  const [departments, setDepartments] = useState<UserGroup[]>([]);
  const [snags, setSnags] = useState<Snag[]>([]);
  const [diaries, setDiaries] = useState<DailySiteDiary[]>([]);
  const [leaveRequests, setLeaveRequests] = useState<LeaveRequest[]>([]);
  const [tasks, setTasks] = useState<SnagTask[]>([]);
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<LeaveRequestStatus | ''>('pending');
  const [taskStatusFilter, setTaskStatusFilter] = useState<SnagTaskStatus | ''>('');
  const [diaryForm, setDiaryForm] = useState(emptyDiaryForm);
  const [leaveForm, setLeaveForm] = useState(emptyLeaveForm);
  const [taskForm, setTaskForm] = useState(emptyTaskForm);

  const members = useMemo(() => {
    const byId = new Map<string, User>();
    departments.forEach(department => department.members.forEach(member => byId.set(member.id, member)));
    return Array.from(byId.values());
  }, [departments]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [departmentResponse, snagResponse, diaryList, leaveList, taskList] = await Promise.all([
        UserGroupsService.getDepartments(),
        SnagTrackerService.getSnags(),
        OperationsService.getDiaries({
          startDate: monthStart(),
          endDate: today(),
          departmentId: departmentFilter || undefined,
        }),
        OperationsService.getLeaveRequests(statusFilter || undefined),
        OperationsService.getSnagTasks({ status: taskStatusFilter || undefined }),
      ]);
      setDepartments(departmentResponse.departments);
      setSnags(snagResponse.snags);
      setDiaries(diaryList);
      setLeaveRequests(leaveList);
      setTasks(taskList);
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Operations Failed To Load',
        message: error instanceof Error ? error.message : 'Please try again.',
      });
    } finally {
      setLoading(false);
    }
  }, [addToast, departmentFilter, statusFilter, taskStatusFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const saveDiary = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      await OperationsService.createDiary({
        ...diaryForm,
        departmentId: diaryForm.departmentId || undefined,
        photoUrls: diaryForm.photoUrls.split(/\n|,/).map(url => url.trim()).filter(Boolean),
      });
      setDiaryForm(emptyDiaryForm);
      await loadData();
      addToast({ type: 'success', title: 'Diary Saved', message: 'The site diary is now on record.' });
    } catch (error) {
      addToast({ type: 'error', title: 'Could Not Save Diary', message: error instanceof Error ? error.message : 'Please try again.' });
    }
  };

  const approveDiary = async (diary: DailySiteDiary) => {
    try {
      await OperationsService.approveDiary(diary.id);
      await loadData();
      addToast({ type: 'success', title: 'Diary Approved' });
    } catch (error) {
      addToast({ type: 'error', title: 'Could Not Approve Diary', message: error instanceof Error ? error.message : 'Please try again.' });
    }
  };

  const saveLeaveRequest = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      await OperationsService.createLeaveRequest({
        ...leaveForm,
        departmentId: leaveForm.departmentId || undefined,
      });
      setLeaveForm(emptyLeaveForm);
      await loadData();
      addToast({ type: 'success', title: 'Request Submitted', message: 'Managers can now review it.' });
    } catch (error) {
      addToast({ type: 'error', title: 'Could Not Submit Request', message: error instanceof Error ? error.message : 'Please try again.' });
    }
  };

  const reviewLeave = async (request: LeaveRequest, action: 'approve' | 'reject') => {
    try {
      if (action === 'approve') await OperationsService.approveLeaveRequest(request.id);
      else await OperationsService.rejectLeaveRequest(request.id);
      await loadData();
      addToast({ type: 'success', title: action === 'approve' ? 'Request Approved' : 'Request Rejected' });
    } catch (error) {
      addToast({ type: 'error', title: 'Review Failed', message: error instanceof Error ? error.message : 'Please try again.' });
    }
  };

  const saveTask = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      await OperationsService.createSnagTask({
        ...taskForm,
        assignedToId: taskForm.assignedToId || undefined,
        dueDate: taskForm.dueDate || undefined,
      });
      setTaskForm(emptyTaskForm);
      await loadData();
      addToast({ type: 'success', title: 'Task Created', message: 'The snag now has an actionable task.' });
    } catch (error) {
      addToast({ type: 'error', title: 'Could Not Create Task', message: error instanceof Error ? error.message : 'Please try again.' });
    }
  };

  const updateTaskStatus = async (task: SnagTask, status: SnagTaskStatus) => {
    try {
      await OperationsService.updateSnagTask(task.id, { status });
      await loadData();
      addToast({ type: 'success', title: 'Task Updated' });
    } catch (error) {
      addToast({ type: 'error', title: 'Could Not Update Task', message: error instanceof Error ? error.message : 'Please try again.' });
    }
  };

  return (
    <main className="operations-page">
      <header className="operations-header">
        <div>
          <p className="operations-eyebrow">Site Operations</p>
          <h1>Daily Diary, Leave, and Snag Tasks</h1>
        </div>
        <button className="operations-button secondary" onClick={loadData} disabled={loading}>
          <FiRefreshCw /> {loading ? 'Refreshing' : 'Refresh'}
        </button>
      </header>

      <nav className="operations-tabs" aria-label="Operations sections">
        <button className={activeTab === 'diary' ? 'active' : ''} onClick={() => setActiveTab('diary')}>
          <FiClipboard /> Site Diary
        </button>
        <button className={activeTab === 'leave' ? 'active' : ''} onClick={() => setActiveTab('leave')}>
          <FiUserCheck /> Availability & Leave
        </button>
        <button className={activeTab === 'tasks' ? 'active' : ''} onClick={() => setActiveTab('tasks')}>
          <FiCheck /> Snag Tasks
        </button>
      </nav>

      {activeTab === 'diary' && (
        <section className="operations-grid">
          <form className="operations-panel" onSubmit={saveDiary}>
            <h2>Log Daily Site Diary</h2>
            <div className="operations-form-grid">
              <label>Date<input type="date" value={diaryForm.diaryDate} onChange={event => setDiaryForm({ ...diaryForm, diaryDate: event.target.value })} required /></label>
              <label>Department<select value={diaryForm.departmentId} onChange={event => setDiaryForm({ ...diaryForm, departmentId: event.target.value })}>
                <option value="">No department</option>
                {departments.map(department => <option key={department.id} value={department.id}>{department.name}</option>)}
              </select></label>
              <label>Weather<input value={diaryForm.weather} onChange={event => setDiaryForm({ ...diaryForm, weather: event.target.value })} placeholder="Dry, windy, 12C" /></label>
              <label>Manpower<input value={diaryForm.manpowerSummary} onChange={event => setDiaryForm({ ...diaryForm, manpowerSummary: event.target.value })} placeholder="8 operatives, 1 supervisor" /></label>
            </div>
            <label>Work completed<textarea value={diaryForm.workCompleted} onChange={event => setDiaryForm({ ...diaryForm, workCompleted: event.target.value })} required /></label>
            <label>Delays<textarea value={diaryForm.delays} onChange={event => setDiaryForm({ ...diaryForm, delays: event.target.value })} /></label>
            <label>Incidents<textarea value={diaryForm.incidents} onChange={event => setDiaryForm({ ...diaryForm, incidents: event.target.value })} /></label>
            <label>Deliveries<textarea value={diaryForm.deliveries} onChange={event => setDiaryForm({ ...diaryForm, deliveries: event.target.value })} /></label>
            <label>Photo URLs<textarea value={diaryForm.photoUrls} onChange={event => setDiaryForm({ ...diaryForm, photoUrls: event.target.value })} placeholder="One URL per line" /></label>
            <button className="operations-button" type="submit">Save diary</button>
          </form>

          <section className="operations-panel">
            <div className="operations-list-header">
              <h2>Recent Diaries</h2>
              <select value={departmentFilter} onChange={event => setDepartmentFilter(event.target.value)}>
                <option value="">All managed departments</option>
                {departments.map(department => <option key={department.id} value={department.id}>{department.name}</option>)}
              </select>
            </div>
            <div className="operations-list">
              {diaries.length === 0 && <p className="operations-empty">No site diaries found for this period.</p>}
              {diaries.map(diary => (
                <article className="operations-card" key={diary.id}>
                  <div>
                    <strong>{new Date(diary.diaryDate).toLocaleDateString()}</strong>
                    <span className={`operations-status ${diary.status}`}>{diary.status}</span>
                  </div>
                  <p>{diary.workCompleted || 'No work summary added.'}</p>
                  <small>{diary.department?.name || 'No department'} · {displayUser(diary.createdBy)}</small>
                  {diary.status !== 'approved' && <button className="operations-icon-button" onClick={() => approveDiary(diary)}><FiCheck /> Approve</button>}
                </article>
              ))}
            </div>
          </section>
        </section>
      )}

      {activeTab === 'leave' && (
        <section className="operations-grid">
          <form className="operations-panel" onSubmit={saveLeaveRequest}>
            <h2>Request Availability or Leave</h2>
            <div className="operations-form-grid">
              <label>Type<select value={leaveForm.type} onChange={event => setLeaveForm({ ...leaveForm, type: event.target.value as any })}>
                <option value="leave">Leave</option>
                <option value="availability">Availability</option>
              </select></label>
              <label>Department<select value={leaveForm.departmentId} onChange={event => setLeaveForm({ ...leaveForm, departmentId: event.target.value })}>
                <option value="">No department</option>
                {departments.map(department => <option key={department.id} value={department.id}>{department.name}</option>)}
              </select></label>
              <label>Start date<input type="date" value={leaveForm.startDate} onChange={event => setLeaveForm({ ...leaveForm, startDate: event.target.value })} required /></label>
              <label>End date<input type="date" value={leaveForm.endDate} onChange={event => setLeaveForm({ ...leaveForm, endDate: event.target.value })} required /></label>
            </div>
            <label>Reason<textarea value={leaveForm.reason} onChange={event => setLeaveForm({ ...leaveForm, reason: event.target.value })} /></label>
            <button className="operations-button" type="submit">Submit request</button>
          </form>

          <section className="operations-panel">
            <div className="operations-list-header">
              <h2>Approval Queue</h2>
              <select value={statusFilter} onChange={event => setStatusFilter(event.target.value as LeaveRequestStatus | '')}>
                <option value="pending">Pending</option>
                <option value="">All</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            <div className="operations-list">
              {leaveRequests.length === 0 && <p className="operations-empty">No requests match this filter.</p>}
              {leaveRequests.map(request => (
                <article className="operations-card" key={request.id}>
                  <div>
                    <strong>{request.type === 'leave' ? 'Leave' : 'Availability'} · {displayUser(request.employee)}</strong>
                    <span className={`operations-status ${request.status}`}>{request.status}</span>
                  </div>
                  <p>{request.startDate} to {request.endDate}</p>
                  <small>{request.department?.name || 'No department'} · {request.reason || 'No reason supplied'}</small>
                  {request.status === 'pending' && (
                    <div className="operations-actions">
                      <button className="operations-icon-button" onClick={() => reviewLeave(request, 'approve')}><FiCheck /> Approve</button>
                      <button className="operations-icon-button danger" onClick={() => reviewLeave(request, 'reject')}><FiX /> Reject</button>
                    </div>
                  )}
                </article>
              ))}
            </div>
          </section>
        </section>
      )}

      {activeTab === 'tasks' && (
        <section className="operations-grid">
          <form className="operations-panel" onSubmit={saveTask}>
            <h2>Create Task From Snag</h2>
            <label>Snag<select value={taskForm.snagId} onChange={event => setTaskForm({ ...taskForm, snagId: event.target.value })} required>
              <option value="">Select snag</option>
              {snags.map(snag => <option key={snag.id} value={snag.id}>{snag.title}</option>)}
            </select></label>
            <label>Title<input value={taskForm.title} onChange={event => setTaskForm({ ...taskForm, title: event.target.value })} required /></label>
            <label>Description<textarea value={taskForm.description} onChange={event => setTaskForm({ ...taskForm, description: event.target.value })} /></label>
            <div className="operations-form-grid">
              <label>Assignee<select value={taskForm.assignedToId} onChange={event => setTaskForm({ ...taskForm, assignedToId: event.target.value })}>
                <option value="">Unassigned</option>
                {members.map(member => <option key={member.id} value={member.id}>{displayUser(member)}</option>)}
              </select></label>
              <label>Due date<input type="date" value={taskForm.dueDate} onChange={event => setTaskForm({ ...taskForm, dueDate: event.target.value })} /></label>
            </div>
            <button className="operations-button" type="submit">Create task</button>
          </form>

          <section className="operations-panel">
            <div className="operations-list-header">
              <h2>Task Board</h2>
              <select value={taskStatusFilter} onChange={event => setTaskStatusFilter(event.target.value as SnagTaskStatus | '')}>
                <option value="">All tasks</option>
                <option value="todo">To do</option>
                <option value="in_progress">In progress</option>
                <option value="blocked">Blocked</option>
                <option value="done">Done</option>
              </select>
            </div>
            <div className="operations-list">
              {tasks.length === 0 && <p className="operations-empty">No snag tasks yet.</p>}
              {tasks.map(task => (
                <article className="operations-card" key={task.id}>
                  <div>
                    <strong>{task.title}</strong>
                    <span className={`operations-status ${task.status}`}>{task.status.replace('_', ' ')}</span>
                  </div>
                  <p>{task.description || task.snag?.title || 'No description added.'}</p>
                  <small>{displayUser(task.assignedTo)} · Due {task.dueDate || 'not set'}</small>
                  <select value={task.status} onChange={event => updateTaskStatus(task, event.target.value as SnagTaskStatus)}>
                    <option value="todo">To do</option>
                    <option value="in_progress">In progress</option>
                    <option value="blocked">Blocked</option>
                    <option value="done">Done</option>
                  </select>
                </article>
              ))}
            </div>
          </section>
        </section>
      )}
    </main>
  );
};

export default OperationsPage;
